import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { EvalSample, TrainedModel } from '../data/types';
import { bucketAccuracy, capture } from '../lib/analytics';
import { browserImageTrainer } from '../ml/browserTrainer';
import { scoreEval, type EvalResult } from '../ml/evalScore';
import type { ImageTrainer, LoadedModel } from '../ml/trainer';
import { useObjectUrl } from './useObjectUrl';
import './EvalPanel.css';

/**
 * Held-out evaluation UI ("Can you fool it?").
 *
 * The kid:
 *   1. Picks the truth — what should this picture be?
 *   2. Uploads (or drops) a picture they took since training.
 *   3. The model predicts; we save the picture as an eval sample.
 *   4. ✓ if the model agreed with the kid's truth, ✗ if it didn't —
 *      a "fool it!" moment, framed as a game.
 *
 * Reported held-out accuracy only counts samples whose `createdAt` is
 * strictly after the trained model's `trainedAt`.
 */

export interface EvalPanelProps {
  projectId: string;
  trained: TrainedModel;
  samples: EvalSample[];
  onAddEvalSample: (classId: string, blob: Blob) => Promise<EvalSample>;
  onDeleteEvalSample: (sampleId: string) => Promise<void>;
  onClose: () => void;
  /** For tests; production uses the real browser trainer. */
  trainer?: ImageTrainer;
}

export function EvalPanel({
  projectId: _projectId,
  trained,
  samples,
  onAddEvalSample,
  onDeleteEvalSample,
  onClose,
  trainer = browserImageTrainer,
}: EvalPanelProps): JSX.Element {
  const labels = trained.labels;
  const [truthClassId, setTruthClassId] = useState<string>(labels[0]?.classId ?? '');

  // `quiz_opened` — fired once on mount. EvalPanel only mounts when
  // the kid clicks "Can you fool it?", so mount === intent.
  useEffect(() => {
    capture('quiz_opened', { kind: 'image' });
  }, []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [predictionsBySampleId, setPredictionsBySampleId] = useState<Record<string, number[]>>({});
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [model, setModel] = useState<LoadedModel | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingImgRef = useRef<HTMLImageElement>(null);
  const truthGroupId = useId();
  const pendingUrl = useObjectUrl(pendingFile);

  // Load the trained model once for the lifetime of the panel.
  useEffect(() => {
    let cancelled = false;
    let opened: LoadedModel | null = null;
    (async (): Promise<void> => {
      try {
        const loaded = await trainer.loadModel(trained.projectId);
        if (cancelled) {
          if (loaded) trainer.disposeModel(loaded);
          return;
        }
        if (!loaded) {
          setError('No trained model is loaded. Train it first.');
          return;
        }
        opened = loaded;
        setModel(loaded);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not load the trained model.');
        }
      }
    })();
    return () => {
      cancelled = true;
      if (opened) {
        trainer.disposeModel(opened);
      }
    };
  }, [trained.projectId, trainer]);

  // Re-score every persisted eval sample when the model becomes ready or
  // the sample list changes. Building an HTMLImageElement per sample is
  // enough for prediction; the bitmap resize happens inside trainer.predict.
  useEffect(() => {
    if (!model) return;
    let cancelled = false;
    (async (): Promise<void> => {
      const next: Record<string, number[]> = {};
      for (const sample of samples) {
        if (cancelled) return;
        if (predictionsBySampleId[sample.id]) continue;
        const url = URL.createObjectURL(sample.blob);
        try {
          const img = await loadImage(url);
          const probs = await trainer.predict(model, img);
          if (cancelled) return;
          next[sample.id] = probs;
        } catch {
          // Per-sample failures don't break the panel; the sample shows up
          // with no prediction (treated as incorrect by scoreEval).
        } finally {
          URL.revokeObjectURL(url);
        }
      }
      if (!cancelled && Object.keys(next).length > 0) {
        setPredictionsBySampleId((prev) => ({ ...prev, ...next }));
      }
    })();
    return () => {
      cancelled = true;
    };
    // We only want to react to changes in the model and the underlying
    // sample set, not to re-run on each prediction we just stored.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, samples, trainer]);

  const score = useMemo(
    () => scoreEval(trained, samples, predictionsBySampleId),
    [trained, samples, predictionsBySampleId],
  );

  const submitPending = async (): Promise<void> => {
    if (!pendingFile || !truthClassId || busy) return;
    if (!model) {
      setError('The model isn’t loaded yet. Try again in a moment.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const img = pendingImgRef.current;
      if (!img) {
        throw new Error('Could not read the picture to test.');
      }
      if (!img.complete) {
        await new Promise<void>((resolve) => {
          img.addEventListener('load', () => resolve(), { once: true });
        });
      }
      const probs = await trainer.predict(model, img);
      const saved = await onAddEvalSample(truthClassId, pendingFile);
      const nextPredictions = { ...predictionsBySampleId, [saved.id]: probs };
      setPredictionsBySampleId(nextPredictions);
      // `quiz_scored` — the score recomputes from `samples` + the new
      // prediction, so fire once we know the new totals are about to
      // render.
      const nextScore = scoreEval(trained, [...samples, saved], nextPredictions);
      capture('quiz_scored', {
        held_out_total: nextScore.heldOutTotal,
        held_out_correct: nextScore.heldOutCorrect,
        accuracy_bucket:
          nextScore.heldOutTotal > 0
            ? bucketAccuracy(nextScore.heldOutCorrect / nextScore.heldOutTotal)
            : 'low',
      });
      setPendingFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not test that picture.');
    } finally {
      setBusy(false);
    }
  };

  const handleFiles = (files: FileList | null): void => {
    if (!files || files.length === 0) return;
    const next = Array.from(files).find((f) => f.type.startsWith('image/'));
    if (next) setPendingFile(next);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setIsDragOver(false);
    handleFiles(e.dataTransfer?.files ?? null);
  };

  const accuracyText =
    score.heldOutTotal === 0
      ? 'No tests yet. Try a picture above.'
      : `${score.heldOutCorrect} of ${score.heldOutTotal} right (${Math.round(
          score.heldOutAccuracy * 100,
        )}%)`;

  return (
    <section className="bl-eval" aria-label="Quiz the model">
      <header className="bl-eval__header">
        <div>
          <h3 className="bl-eval__title">Can you fool it?</h3>
          <p className="bl-eval__hint">
            Pick what the picture should be, then drop it in. The model gets a ✓ if it guesses
            right, ✗ if you fooled it.
          </p>
        </div>
        <button type="button" className="bl-eval__close" onClick={onClose}>
          Close
        </button>
      </header>

      <fieldset className="bl-eval__truth">
        <legend className="bl-eval__truth-legend">What should this picture be?</legend>
        <div className="bl-eval__truth-options">
          {labels.map((label) => (
            <label
              key={label.classId}
              className={`bl-eval__truth-option${
                truthClassId === label.classId ? ' bl-eval__truth-option--active' : ''
              }`}
            >
              <input
                type="radio"
                name={truthGroupId}
                value={label.classId}
                checked={truthClassId === label.classId}
                onChange={() => setTruthClassId(label.classId)}
              />
              {label.name}
            </label>
          ))}
        </div>
      </fieldset>

      <div
        className={`bl-eval__dropzone${isDragOver ? ' bl-eval__dropzone--drag' : ''}`}
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        aria-label="Drop a test picture here"
      >
        {pendingUrl ? (
          <img
            ref={pendingImgRef}
            key={pendingUrl}
            src={pendingUrl}
            alt="Sample about to be tested"
            aria-label="Sample queued for testing"
            className="bl-eval__pending"
          />
        ) : (
          <p className="bl-eval__dropzone-hint">
            {isDragOver ? 'Drop the picture here.' : 'Drop a picture or use the button below.'}
          </p>
        )}
      </div>

      <div className="bl-eval__actions">
        <button
          type="button"
          className="bl-eval__pick-button"
          onClick={() => fileInputRef.current?.click()}
        >
          Choose a picture
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="bl-eval__file-input"
          onChange={(e) => handleFiles(e.target.files)}
          aria-label="Pick a test picture"
        />
        <button
          type="button"
          className="bl-eval__submit"
          onClick={() => void submitPending()}
          disabled={!pendingFile || !truthClassId || busy}
        >
          {busy ? 'Testing…' : 'Test it'}
        </button>
      </div>

      {error && (
        <p role="alert" className="bl-eval__error">
          {error}
        </p>
      )}

      <div className="bl-eval__score" role="status" aria-live="polite">
        <span
          className="bl-eval__score-text"
          data-testid="eval-score"
          data-held-out-total={score.heldOutTotal}
          data-held-out-correct={score.heldOutCorrect}
        >
          {accuracyText}
        </span>
        {score.staleTotal > 0 && (
          <span className="bl-eval__stale">
            {' '}
            ({score.staleTotal} from before retraining, not counted)
          </span>
        )}
      </div>

      {score.results.length > 0 && (
        <ol className="bl-eval__results" aria-label="Test results">
          {[...score.results].reverse().map((result) => (
            <EvalResultRow
              key={result.sample.id}
              result={result}
              onDelete={() => void onDeleteEvalSample(result.sample.id)}
            />
          ))}
        </ol>
      )}
    </section>
  );
}

function EvalResultRow({
  result,
  onDelete,
}: {
  result: EvalResult;
  onDelete: () => void;
}): JSX.Element {
  const url = useObjectUrl(result.sample.blob);
  const truthName = result.sample.classId;
  return (
    <li
      className={`bl-eval__row${
        result.correct ? ' bl-eval__row--correct' : ' bl-eval__row--wrong'
      }${result.heldOut ? '' : ' bl-eval__row--stale'}`}
    >
      <div className="bl-eval__row-thumb">{url && <img src={url} alt="Test sample" />}</div>
      <div className="bl-eval__row-info">
        <span className="bl-eval__row-mark" aria-hidden="true">
          {result.correct ? '✓' : '✗'}
        </span>
        <span className="bl-eval__row-summary">
          {result.correct ? (
            <>
              The model got <strong>{result.predictedClassName ?? '?'}</strong>.
            </>
          ) : (
            <>
              You said <strong>{result.predictedClassName ? truthName : truthName}</strong>; the
              model said <strong>{result.predictedClassName ?? 'nothing'}</strong>.
            </>
          )}
        </span>
        {!result.heldOut && <span className="bl-eval__row-stale">From before this training</span>}
      </div>
      <button
        type="button"
        className="bl-eval__row-delete"
        onClick={onDelete}
        aria-label="Remove this test"
      >
        ×
      </button>
    </li>
  );
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    let settled = false;
    img.onload = () => {
      if (settled) return;
      settled = true;
      resolve(img);
    };
    img.onerror = () => {
      if (settled) return;
      settled = true;
      reject(new Error('Could not decode that picture.'));
    };
    img.src = src;
    // jsdom never fires load/error for blob URLs because there's no real
    // image decoder. The trainer stub ignores the actual pixels, so giving
    // it a not-yet-decoded HTMLImageElement is fine. The 50ms is a no-op
    // in real browsers, where load fires far faster than this.
    setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve(img);
    }, 50);
  });
}
